import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { ReviewStrip } from "@/components/review-strip";
import { FEES, FIRM, NO_CHARGE_GUARANTEE, faqSchema } from "@/lib/firm";
import { CONVEYANCING_LOCATIONS } from "@/lib/conveyancing-locations";

const FAQS = [
  {
    q: "What is the conveyancing No Charge Guarantee?",
    a: `This guarantee applies only to conveyancing, not to our other legal services. ${NO_CHARGE_GUARANTEE.long}`,
  },
  {
    q: "How much is conveyancing in Caloundra?",
    a: `Our fixed fee for a residential sale is ${FEES.sale} including GST and for a residential purchase ${FEES.purchase} including GST. On top of the legal fee you pay disbursements — searches, registration fees and, for buyers, transfer duty — which go to third parties at cost.`,
  },
  {
    q: "How long does a conveyance take in Queensland?",
    a: "Most residential contracts settle 30 days from the contract date, though 21, 42 and 60 day settlements are all common. The sequence is cooling-off (5 business days), building and pest and finance (usually 14–21 days), then settlement.",
  },
  {
    q: "Can you review a contract before I sign?",
    a: "Yes, and we would rather you did. Once you sign, your options narrow to the cooling-off period and whatever conditions are in the contract. A pre-signing review costs nothing extra when we go on to act.",
  },
  {
    q: "Do you act on off-the-plan and body corporate purchases?",
    a: "Yes. Unit and townhouse purchases involve body corporate searches, a disclosure statement and levy adjustments, and off-the-plan contracts carry extra disclosure rights and sunset date issues. We handle both.",
  },
  {
    q: "Do I need to come into the office?",
    a: "Not necessarily. Settlements in Queensland are electronic and documents can be signed and verified remotely. If you would prefer to sit down together in Golden Beach, we are happy to.",
  },
];

const STEPS = [
  {
    t: "1. Before you sign",
    d: "Send us the contract. We explain the terms, check the disclosure, and tell you what to change or negotiate. If you have already signed, we get straight onto your cooling-off and condition dates.",
  },
  {
    t: "2. Conditions",
    d: "We diarise every date and chase them: cooling-off, building and pest, finance approval, and any special conditions. You get a reminder before each one, not after.",
  },
  {
    t: "3. Searches and enquiries",
    d: "Title, plan, council rates, water, body corporate and any state searches relevant to your property — reviewed, not just filed.",
  },
  {
    t: "4. Settlement",
    d: "We prepare the settlement statement and adjustments, liaise with your bank and the other side, and attend electronic settlement. You get a call the moment it is done.",
  },
];

export const Route = createFileRoute("/conveyancing/")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(`Fixed-Fee Conveyancing Caloundra | ${FIRM.name}`) },
      {
        name: "description",
        content: fitDescription(`Fixed-fee conveyancing from Golden Beach lawyers. Sales ${FEES.sale}, purchases $1,499 incl. GST. Contract reviews, searches and settlement.`),
      },

      { property: "og:title", content: fitTitle(`Fixed-Fee Conveyancing | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Local Sunshine Coast conveyancing lawyers. Fixed fee agreed before we start, no hourly billing."),
      },
      { property: "og:url", content: "https://coastsidelaw.com.au/conveyancing" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/conveyancing" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: ConveyancingPage,
});

function ConveyancingPage() {
  return (
    <>
      <PageHero
        eyebrow="Property law"
        title="Conveyancing on the Sunshine Coast, at a fixed fee"
        lead="Buying or selling in Caloundra, Golden Beach, Pelican Waters or anywhere on the Coast? We agree the fee before we start, watch every date, and tell you what things mean in plain English."
        personSlug="erin-wilkinson"
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">What it costs</h2>
          <dl className="mt-5 space-y-4 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-4">
              <dt className="font-medium text-foreground">Residential sale</dt>
              <dd className="font-serif text-2xl text-foreground">
                {FEES.sale} <span className="text-sm font-sans text-muted-foreground">{FEES.saleNote}</span>
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="font-medium text-foreground">Residential purchase</dt>
              <dd className="font-serif text-2xl text-foreground">
                {FEES.purchase}{" "}
                <span className="text-sm font-sans text-muted-foreground">{FEES.purchaseNote}</span>
              </dd>
            </div>
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            Disbursements — searches, Titles Office registration fees and, for buyers, transfer duty —
            are additional and are paid to third parties at cost.{" "}
            <Link to="/fees" className="font-semibold text-primary underline">
              See the full fee breakdown
            </Link>
            .
          </p>
        </div>

        <section aria-labelledby="conveyancing-guarantee" className="mt-10 border-2 border-accent bg-accent/15 p-7">
          <div className="flex items-start gap-4">
            <ShieldCheck className="mt-1 size-8 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <h2 id="conveyancing-guarantee" className="font-serif text-2xl text-foreground">Conveyancing {NO_CHARGE_GUARANTEE.title}</h2>
              <p className="mt-2 text-sm font-semibold text-foreground">Applies only to conveyancing, not to our other legal services.</p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{NO_CHARGE_GUARANTEE.long}</p>
              <Link to="/fees" className="mt-4 inline-block text-sm font-semibold text-primary underline">See conveyancing fees</Link>
            </div>
          </div>
        </section>

        <h2 className="mt-16 font-serif text-3xl text-foreground">How it works</h2>
        <div className="mt-8 space-y-6">
          {STEPS.map((s) => (
            <div key={s.t} className="border-l-2 border-accent pl-5">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">What we handle</h2>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {[
            "House and land purchases and sales",
            "Units, townhouses and body corporate schemes",
            "Off-the-plan contracts and sunset dates",
            "Auction purchases (no cooling-off — get advice first)",
            "Transfers between family members and related parties",
            "Contract reviews and pre-signing advice",
            "Extensions, disputes and terminations",
            "Transfer duty concessions and first home buyers",
          ].map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who handles your conveyancing" />

        <p className="mt-10 text-base leading-relaxed text-muted-foreground">
          Selling rather than buying? The{" "}
          <Link to="/selling" className="font-semibold text-primary underline">
            selling a property page
          </Link>{" "}
          sets out the seller disclosure obligations, the fixed fee and how a sale runs from listing
          to settlement.
        </p>


        <h2 className="mt-16 font-serif text-3xl text-foreground">Conveyancing by area</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Looking for a conveyancer near you around Caloundra?{" "}
          <Link to="/conveyancing-near-me" className="font-semibold text-primary underline">
            See our local page for Golden Beach, Caloundra, Pelican Waters and Baringa
          </Link>
          .
        </p>
        <ul className="mt-6 flex flex-wrap gap-2">
          {CONVEYANCING_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/conveyancing/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Conveyancing {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-10 rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <h3 className="font-serif text-xl text-foreground">Buying or selling commercial property?</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Offices, retail, industrial and tenanted investments work differently — no cooling-off
            period, GST consequences and leases you take on.
          </p>
          <Link
            to="/commercial-conveyancing"
            className="mt-3 inline-block text-sm font-semibold text-primary underline underline-offset-4"
          >
            Commercial conveyancing
          </Link>
          {" · "}
          <Link
            to="/conveyancing-services"
            className="mt-3 inline-block text-sm font-semibold text-primary underline underline-offset-4"
          >
            All conveyancing services
          </Link>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Questions buyers and sellers ask</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Get a fixed-fee conveyancing quote" />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5">
        <ReviewStrip />
      </section>

      <CtaBand
        title="Got a contract in front of you?"
        body={`Send it through before you sign, or call ${FIRM.phone} and we will talk it through.`}
      />
      <div className="h-20" />
    </>
  );
}
